"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";

// Hardcoded hero slider data
const heroSliders = [
  {
    id: 1,
    title: "",
    excerpt: "",
    imageUrl: "/images/hero1.webp",
    mobileImageUrl: "/images/hero1-mobile.webp",
    buttonText: "",
    buttonLink: "",
    linkTarget: "_self",
  }
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSliders.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSliders.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroSliders.length) % heroSliders.length
    );
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlider = heroSliders[currentSlide];

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Images */}
      <div className="absolute inset-0">
        {heroSliders.map((slider, index) => (
          <div
            key={slider.id}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out ${
              index === currentSlide
                ? "opacity-100 scale-100"
                : "opacity-0 scale-105"
            }`}
            style={{
              backgroundImage: `url(${
                isMobile && slider.mobileImageUrl
                  ? slider.mobileImageUrl
                  : slider.imageUrl
              })`,
            }}
          />
        ))}
      </div>

      {/* Gradient Overlay for better text readability */}
      {/* <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/60"></div> */}

      {/* Content Overlay - Bottom and Center */}
      <div className="absolute inset-0 flex flex-col justify-end z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-4xl mx-auto text-center">
            {currentSlider.excerpt && (
              <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
                {currentSlider.excerpt}
              </p>
            )}
            {currentSlider.buttonText && currentSlider.buttonLink && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-transparent hover:bg-white/20 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 border-2 border-white/50 hover:border-white"
                  onClick={() => {
                    if (currentSlider.linkTarget === "_blank") {
                      window.open(currentSlider.buttonLink, "_blank");
                    } else {
                      window.location.href = currentSlider.buttonLink;
                    }
                  }}
                >
                  {currentSlider.buttonText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Only show if more than 1 slide */}
      {heroSliders.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Play/Pause Button - Only show if more than 1 slide */}
      {heroSliders.length > 1 && (
        <button
          onClick={togglePlayPause}
          className="absolute bottom-8 right-8 z-20 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
          aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
      )}
    </section>
  );
}
